import { useState, type FormEvent } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Droplets,
  Info,
  Loader2,
  Play,
  RefreshCw,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTenantId } from '@/lib/auth/hooks';
import { CubixApiError } from '@/lib/api/types';
import { cn } from '@/lib/utils';

import { AnalysisSkeleton } from '@/components/shared/AnalysisSkeleton';

import { useLatestLcr, useTriggerLcr } from './api';
import type { LcrNsfrDetail } from './types';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Phase 2.0.LCR — Basel III LCR / NSFR on-demand calculation page.
 *
 * Layout:
 *   1. NSFR placeholder banner (when nsfrDataAvailable=false)
 *   2. Calculate / Reload form
 *   3. LCR compliance tiles (ratio, HQLA, net outflows)
 *   4. NSFR compliance tiles (ratio, ASF, RSF)
 */
export function LiquidityAnalysisPage() {
  const tenantId = useTenantId();
  const [date, setDate] = useState<string>(todayISO());

  const latest  = useLatestLcr(date);
  const trigger = useTriggerLcr();

  const detail: LcrNsfrDetail | null = latest.data ?? null;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    trigger.mutate({ calculationDate: date, force: true });
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Droplets className="h-5 w-5 text-primary" />
          Liquidity — LCR / NSFR
        </h1>
        <p className="text-sm text-muted-foreground">
          Basel III liquidity ratios — threshold ≥ 100% — tenant{' '}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{tenantId ?? '—'}</code>.
        </p>
      </header>

      <NsfrPlaceholderBanner detail={detail} />

      {/* Run form */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Run LCR / NSFR</CardTitle>
          <CardDescription>
            Reads pre-aggregated HQLA, 30-day net outflows, ASF, and RSF from the
            Gold layer and computes both ratios in a single pass.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="flex flex-wrap items-end gap-3" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="lcr-date">Calculation date</Label>
              <Input
                id="lcr-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="w-44"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => latest.refetch()}
              disabled={latest.isFetching}
            >
              {latest.isFetching ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Reload
            </Button>
            <Button type="submit" disabled={trigger.isPending}>
              {trigger.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              Run on-demand
            </Button>
          </form>

          {trigger.isError  ? <ApiErrorBanner error={trigger.error} /> : null}
          {trigger.isSuccess ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Calculation complete · result_id{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                {trigger.data.resultId}
              </code>
              {' · '}engine {trigger.data.engineVersion}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {latest.isError ? <ApiErrorBanner error={latest.error} /> : null}

      {latest.isLoading && !detail ? (
        <AnalysisSkeleton />
      ) : (
        <>
          {/* LCR section */}
          <LcrSection detail={detail} loading={latest.isLoading} />

          {/* NSFR section */}
          <NsfrSection detail={detail} loading={latest.isLoading} />
        </>
      )}
    </div>
  );
}

// ── NSFR placeholder banner ───────────────────────────────────────────────────

function NsfrPlaceholderBanner({ detail }: { detail: LcrNsfrDetail | null }) {
  if (!detail || detail.nsfrDataAvailable) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-800">
      <Info className="mt-0.5 h-4 w-4 shrink-0" />
      <p>
        <strong>NSFR data not yet available.</strong> The Gold layer does not yet
        publish ASF / RSF aggregates for this tenant. NSFR ratios will show as 0%
        until the dbt Gold pipeline populates{' '}
        <code className="rounded bg-muted px-1 py-0.5 text-xs">gold.lcr_classification</code>{' '}
        with ASF and RSF columns.
      </p>
    </div>
  );
}

// ── LCR section ───────────────────────────────────────────────────────────────

function LcrSection({
  detail,
  loading,
}: {
  detail: LcrNsfrDetail | null;
  loading: boolean;
}) {
  const ratio    = detail?.lcrRatio    ? Number(detail.lcrRatio)    : null;
  const hqla     = detail?.lcrHqla     ? Number(detail.lcrHqla)     : null;
  const outflows = detail?.lcrNetOutflows ? Number(detail.lcrNetOutflows) : null;
  const compliant = detail?.lcrCompliant ?? null;

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Liquidity Coverage Ratio (LCR)
      </h2>
      <div className="grid gap-4 sm:grid-cols-3">
        <RatioCard
          title="LCR ratio"
          value={ratio != null ? formatPct(ratio) : null}
          loading={loading}
          compliant={compliant}
          description={
            compliant === true
              ? 'Above 100% — compliant'
              : compliant === false
                ? 'Below 100% — breach'
                : 'No data for this date'
          }
        />
        <MetricCard
          title="HQLA"
          value={hqla != null ? formatCcy(hqla) : null}
          loading={loading}
          description="High Quality Liquid Assets (numerator)"
        />
        <MetricCard
          title="30-day net outflows"
          value={outflows != null ? formatCcy(outflows) : null}
          loading={loading}
          description="Stress-weighted net cash outflows (denominator)"
        />
      </div>
    </div>
  );
}

// ── NSFR section ──────────────────────────────────────────────────────────────

function NsfrSection({
  detail,
  loading,
}: {
  detail: LcrNsfrDetail | null;
  loading: boolean;
}) {
  const ratio    = detail?.nsfrRatio ? Number(detail.nsfrRatio) : null;
  const asf      = detail?.nsfrAsf   ? Number(detail.nsfrAsf)   : null;
  const rsf      = detail?.nsfrRsf   ? Number(detail.nsfrRsf)   : null;
  const compliant = detail?.nsfrCompliant ?? null;
  const available = detail?.nsfrDataAvailable ?? true;

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Net Stable Funding Ratio (NSFR)
        {!available && (
          <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
            placeholder data
          </span>
        )}
      </h2>
      <div className="grid gap-4 sm:grid-cols-3">
        <RatioCard
          title="NSFR ratio"
          value={ratio != null && available ? formatPct(ratio) : null}
          loading={loading}
          compliant={available ? compliant : null}
          description={
            !available
              ? 'Awaiting Gold layer ASF / RSF data'
              : compliant === true
                ? 'Above 100% — compliant'
                : compliant === false
                  ? 'Below 100% — breach'
                  : 'No data for this date'
          }
        />
        <MetricCard
          title="Available Stable Funding (ASF)"
          value={asf != null && available ? formatCcy(asf) : null}
          loading={loading}
          description="Stable funding available over 1-year horizon (numerator)"
        />
        <MetricCard
          title="Required Stable Funding (RSF)"
          value={rsf != null && available ? formatCcy(rsf) : null}
          loading={loading}
          description="Stable funding required by assets / off-balance (denominator)"
        />
      </div>
    </div>
  );
}

// ── Shared tile components ────────────────────────────────────────────────────

function RatioCard({
  title,
  value,
  loading,
  compliant,
  description,
}: {
  title: string;
  value: string | null;
  loading: boolean;
  compliant: boolean | null;
  description: string;
}) {
  const isBreaching = compliant === false;
  return (
    <Card className={cn(isBreaching && 'border-destructive/40 bg-destructive/5')}>
      <CardHeader className="pb-2">
        <CardDescription className="flex items-center gap-1.5">
          {compliant === true ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
          ) : compliant === false ? (
            <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
          ) : (
            <Droplets className="h-3.5 w-3.5" />
          )}
          {title}
        </CardDescription>
        <CardTitle
          className={cn(
            'text-2xl',
            isBreaching && 'text-destructive',
            compliant === true && 'text-emerald-700',
          )}
        >
          {value ?? (loading ? '…' : '—')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 text-xs text-muted-foreground">
        {description}
      </CardContent>
    </Card>
  );
}

function MetricCard({
  title,
  value,
  loading,
  description,
}: {
  title: string;
  value: string | null;
  loading: boolean;
  description: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-2xl">
          {value ?? (loading ? '…' : '—')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 text-xs text-muted-foreground">
        {description}
      </CardContent>
    </Card>
  );
}

// ── Formatters ────────────────────────────────────────────────────────────────

function formatPct(decimal: number): string {
  return (decimal * 100).toFixed(1) + '%';
}

function formatCcy(amount: number): string {
  const abs  = Math.abs(amount);
  const sign = amount < 0 ? '−' : '';
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000)     return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)         return `${sign}${(abs / 1_000).toFixed(2)}K`;
  return `${sign}${abs.toFixed(2)}`;
}

// ── Error banner ──────────────────────────────────────────────────────────────

function ApiErrorBanner({ error }: { error: CubixApiError | Error }) {
  const isCubix = error instanceof CubixApiError;
  return (
    <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">{error.message}</p>
        {isCubix ? (
          <p className="mt-0.5 text-xs opacity-80">
            code{' '}
            <code className="rounded bg-background/60 px-1 py-0.5">{error.code}</code>
            {error.field ? <> · field {error.field}</> : null}
          </p>
        ) : null}
      </div>
    </div>
  );
}
